import { Edit, Email, FilterAlt, Refresh } from "@mui/icons-material";
import { Grid, IconButton } from "@mui/material";
import DataGrid from '../../../../components/DataGrid';
import ContentTitle from "../../../../components/Content/ContentTitle";
import { useLanguageContext } from "../../../../contexts/LanguageContext";
import ISystemSettings from "../../../../interfaces/ISystemSettings";
import { useEffect, useCallback, useMemo } from "react";
import FormHelper from "../../../../helpers/FormHelper";
import EditDialog from "./EditDialog";
import { useAuthContext } from "../../../../contexts/AuthContext";
import SettingsService from "../../../../services/SystemSettingsService";
import StringHelper from "../../../../helpers/StringHelper";
import { INavigationLink } from "../../../../interfaces/INavigation";
import Filters from "./Filters";
import Tooltip from "../../../../components/Tooltip";
import ArrayHelper from "../../../../helpers/ArrayHelper";
import useDrawer from "../../../../hooks/useDrawer";
import useDialog from "../../../../hooks/useDialog";
import { useEnvironmentContext } from "../../../../contexts/EnvironmentContext";
import useState from "../../../../hooks/useState";


interface IStateValues {
    filters: FormData | undefined;
    data: ISystemSettings[];
    selectedRow: ISystemSettings | undefined;
}


const SystemSettings = (linkProps: INavigationLink) => {
    const { environment } = useEnvironmentContext();
    const { language, translate } = useLanguageContext();
    const { hasPermission } = useAuthContext();

    const state = useState<IStateValues>({
        filters: undefined,
        data: [],
        selectedRow: undefined,
    });

    const filtersDrawer = useDrawer();
    const editDialog = useDialog();


    useEffect(() => {
        handleRefresh();
        // eslint-disable-next-line
    }, [language, state.values.filters]);
    
    
    const handleRefresh = useCallback(() => {
        const formData = FormHelper.Create(undefined);
        SettingsService.List(formData)
            .then((resp) => {
                const settings = resp.data.systemSettings as ISystemSettings[];
                state.update('data', settings.map(row => {
                    const translationKey = StringHelper.FirstLetterLowerCase(row.alias.replace(/_/g, ''));

                    return {
                        ...row,
                        readOnly: Boolean(row.readOnly),
                        _category: translate(row.category),
                        _description: translate(`_${translationKey}`)
                    };
                }));
            });
        // eslint-disable-next-line
    }, [language]);

    
    const handleTestSendingEmail = useCallback(() => {
        const formData = FormHelper.Create(undefined);
        SettingsService.TestSendingEmail(formData)
        // eslint-disable-next-line
    }, []);


    const handleEdit = useCallback((row: ISystemSettings | undefined) => {
        state.update('selectedRow', row);
        editDialog.handleOpen();
        // eslint-disable-next-line
    }, []);


    const filteredData = useMemo(() => {
        const filters = state.values.filters;

        if (!filters) {
            return state.values.data;
        }

        let filteredData = [...state.values.data];

        const description = (filters.get('description')?.toString() ?? '').trim().toLowerCase();
        if (description.length > 0) {
            filteredData = filteredData.filter(r => r._description.toLowerCase().includes(description));
        }

        const category = (filters.get('category')?.toString() ?? '').split(',');
        if (!category.includes('0') && category.length > 0) {
            filteredData = filteredData.filter(r => category.includes(r.category));
        }

        return filteredData;
        // eslint-disable-next-line
    }, [translate, state.values.data, state.values.filters]);


    const categoryList = useMemo(() => {
        return ArrayHelper.DistinctBy(state.values.data, 'category').map(s => s.category);
        // eslint-disable-next-line
    }, [state.values.data]);


    const dataGridColumns = [
        { 
            field: '_category', 
            headerName: translate('category'),
            flex: 1, 
            minWidth: 200
        }, { 
            field: '_description', 
            headerName: translate('description'), 
            flex: 1, 
            minWidth: 200
        }, { 
            field: 'value', 
            headerName: translate('value'), 
            flex: 1, 
            minWidth: 200,
            renderCell: (params: any) => {
                const value = params.row.value;

                if (params.row.type === 'password') {
                    return [...Array(value.length).keys()].map(() => "•");
                } else if (params.row.type === 'image') {
                    return <img src={value} alt={params.row.type} className='height-70pc' />;
                } else if (params.row.type === 'partnerPmsList') {
                    const partnerPms = environment.partnerPmsList.find(p => p.description === value) ?? environment.partnerPms;
                    
                    return <>
                        <img src={partnerPms.images.icon} alt={partnerPms.description} className='height-40pc marginRight-8px' />
                        {partnerPms.description}
                    </>
                }

                return value;
            }
        }, { 
            field: 'actions', 
            type: 'actions', 
            headerName: translate('actions'), 
            width: 120, 
            renderCell: (params: any) => {
                if (params.row.readOnly) {
                    return null;
                }

                return (
                    <Grid item>
                        {
                            hasPermission(`${linkProps.alias[0]}.Edit`) && (
                                <Tooltip title={translate('edit')}>
                                    <IconButton className='actionbutton-edit' onClick={() => handleEdit(params.row)}>
                                        <Edit fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            )
                        }
                    </Grid>
                );
            }
        }
    ];

    return (
        <>
            <Grid item xs={12} className='grid-header'>
                <Grid item className='grid-title'>
                    <ContentTitle {...linkProps} />
                </Grid>

                <Grid item className='grid-tools'>
                    <Tooltip title={translate('testSendingEmail')}>
                        <IconButton onClick={handleTestSendingEmail}>
                            <Email />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={translate('filter')}>
                        <IconButton onClick={() => filtersDrawer.handleOpen()}>
                            <FilterAlt />
                        </IconButton>
                    </Tooltip>
                    
                    <Tooltip title={translate('refresh')}>
                        <IconButton onClick={handleRefresh}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </Grid>
            </Grid>

            <Grid container className='grid-content'>
                <Grid item xs={12} className='paper-content'>
                    <DataGrid 
                        columns={dataGridColumns} 
                        data={filteredData} 
                        openSearch={false}
                        onCellDoubleClick={params => {
                            if (!hasPermission(`${linkProps.alias[0]}.Edit`) || params.row.readOnly) {
                                return;
                            }
                            
                            handleEdit(params.row);
                        }}
                    />
                </Grid>
            </Grid>

            <Filters
                open={filtersDrawer.open}
                handleClose={() => filtersDrawer.handleClose()}
                categoryList={categoryList}
                handleApplyFilters={(formData) => state.update('filters', formData)}
            />

            <EditDialog
                open={editDialog.open}
                handleClose={() => editDialog.handleClose()}
                data={state.values.selectedRow}
                handleListRefresh={handleRefresh}
            />
        </>
    );
}

export default SystemSettings;